import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { store } from '../store';
import { setWatchlist } from '../store/slices/userSlice';
import { Media } from '../types/media';

class WatchlistService {
  private unsubscribe: (() => void) | null = null;

  initialize() {
    const state = store.getState();
    const userId = state.user?.uid;

    if (userId && !this.unsubscribe) {
      const userDocRef = doc(db, 'users', userId);
      
      this.unsubscribe = onSnapshot(userDocRef, (doc) => {
        if (doc.exists()) {
          const watchlist = doc.data()?.watchlist || [];
          store.dispatch(setWatchlist(watchlist));
        }
      });
    }
  }

  async addToWatchlist(userId: string, media: Media) {
    if (!userId) return;

    const userDocRef = doc(db, 'users', userId);
    await updateDoc(userDocRef, {
      watchlist: arrayUnion(media)
    });
  }

  async removeFromWatchlist(userId: string, mediaId: number) {
    if (!userId) return;

    const state = store.getState();
    const watchlist = state.user?.watchlist || [];
    const mediaToRemove = watchlist.find(item => item.id === mediaId);

    if (mediaToRemove) {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        watchlist: arrayRemove(mediaToRemove)
      });
    }
  }

  async loadWatchlist(userId: string) {
    if (!userId) return;
    const userDocRef = doc(db, 'users', userId);
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      store.dispatch(setWatchlist(data.watchlist || []));
    }
  }

  async toggleMovie(userId: string, movieId: number) {
    if (!userId) return;
    const state = store.getState();
    const watchlist = state.user?.watchlist || [];
    const exists = watchlist.some(item => item.id === movieId && item.type === 'movie');
    
    if (exists) {
      await this.removeFromWatchlist(userId, movieId);
    } else {
      await this.addToWatchlist(userId, { id: movieId, type: 'movie' });
    }
  }

  async toggleShow(userId: string, showId: number) {
    if (!userId) return;
    const state = store.getState();
    const watchlist = state.user?.watchlist || [];
    const exists = watchlist.some(item => item.id === showId && item.type === 'tv');
    
    if (exists) {
      await this.removeFromWatchlist(userId, showId);
    } else {
      await this.addToWatchlist(userId, { id: showId, type: 'tv' });
    }
  }

  stop() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}

export const watchlistService = new WatchlistService();
